import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { formatCurrency } from "@/lib/currency";
import { chartColors, getCategoryColor } from "@/utils/chartColors";

interface ReceiptItem {
  name: string;
  amount: number;
  category: "food" | "household" | "health" | "personal" | "other";
}

interface Receipt {
  id: string; // receipts.id is a UUID
  date: string;
  store: string;
  total: number;
  items: ReceiptItem[];
  imageUrl: string;
  currency?: string;
}

interface SpendingChartProps {
  receipts: Receipt[];
  preferredCurrency: string;
  convertAmount: (amount: number, fromCurrency: string) => number;
}

type CategoryFilter = "all" | "food" | "household" | "health" | "personal" | "other";
type TimePeriod = "week" | "month" | "quarter" | "year" | "all";

export const SpendingChart = ({ receipts, preferredCurrency, convertAmount }: SpendingChartProps) => {
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState<CategoryFilter>("all");
  const [selectedPeriod, setSelectedPeriod] = useState<TimePeriod>("month");

  const categoryOptions = [
    { value: "all" as const, label: t('spendingChart.allCategories') },
    { value: "food" as const, label: t('categories.food') },
    { value: "household" as const, label: t('categories.household') },
    { value: "health" as const, label: t('categories.health') },
    { value: "personal" as const, label: t('categories.personal') },
    { value: "other" as const, label: t('categories.other') },
  ];

  const timePeriodOptions = [
    { value: "week" as const, label: t('categoryBreakdown.periods.lastWeek') },
    { value: "month" as const, label: t('categoryBreakdown.periods.lastMonth') },
    { value: "quarter" as const, label: t('categoryBreakdown.periods.lastQuarter') },
    { value: "year" as const, label: t('categoryBreakdown.periods.lastYear') },
    { value: "all" as const, label: t('categoryBreakdown.periods.allTime') },
  ];

  const getFilteredReceiptsForPieChart = () => {
    if (selectedPeriod === "all") return receipts;

    const now = new Date();
    const cutoffDate = new Date();

    switch (selectedPeriod) {
      case "week":
        cutoffDate.setDate(now.getDate() - 7);
        break;
      case "month":
        cutoffDate.setMonth(now.getMonth() - 1);
        break;
      case "quarter":
        cutoffDate.setMonth(now.getMonth() - 3);
        break;
      case "year":
        cutoffDate.setFullYear(now.getFullYear() - 1);
        break;
    }

    return receipts.filter(receipt => new Date(receipt.date) >= cutoffDate);
  };

  const getFilteredReceiptsForBarChart = () => {
    if (selectedCategory === "all") return receipts;
    
    return receipts.map(receipt => ({
      ...receipt,
      items: receipt.items.filter(item => item.category === selectedCategory),
      total: receipt.items
        .filter(item => item.category === selectedCategory)
        .reduce((sum, item) => sum + item.amount, 0)
    })).filter(receipt => receipt.total > 0);
  };

  // Prepare data for pie chart (filtered by time period) - convert to preferred currency
  const filteredReceiptsForPie = getFilteredReceiptsForPieChart();
  const categoryTotals = filteredReceiptsForPie.reduce((acc, receipt) => {
    receipt.items.forEach(item => {
      const convertedAmount = convertAmount(item.amount, receipt.currency || 'USD');
      acc[item.category] = (acc[item.category] || 0) + convertedAmount;
    });
    return acc;
  }, {} as Record<string, number>);

  const pieData = Object.entries(categoryTotals).map(([category, amount]) => ({
    name: category.charAt(0).toUpperCase() + category.slice(1),
    value: amount,
    fill: getCategoryColor(category as keyof typeof chartColors.categories)
  }));

  // Data for bar chart (filtered by category) - convert to preferred currency
  const filteredReceiptsForBar = getFilteredReceiptsForBarChart();
  const weeklyData = filteredReceiptsForBar.reduce((acc, receipt) => {
    const date = new Date(receipt.date);
    const weekStart = new Date(date.getFullYear(), date.getMonth(), date.getDate() - date.getDay());
    const weekKey = weekStart.toISOString().split('T')[0];
    
    if (!acc[weekKey]) {
      acc[weekKey] = { week: weekKey, total: 0 };
    }
    const convertedTotal = convertAmount(receipt.total, receipt.currency || 'USD');
    acc[weekKey].total += convertedTotal;
    
    return acc;
  }, {} as Record<string, { week: string; total: number }>);

  const barData = Object.values(weeklyData)
    .sort((a, b) => new Date(a.week).getTime() - new Date(b.week).getTime())
    .map(item => ({
      ...item,
      week: new Date(item.week).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    }));

  const getBarColor = () => {
    if (selectedCategory === "all") return chartColors.primary;
    return getCategoryColor(selectedCategory as keyof typeof chartColors.categories);
  };

  const CustomTooltip = ({ active, payload, label }: { active?: boolean; payload?: Array<{ value: number }>; label?: string }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-card border rounded-lg p-3 shadow-custom-md">
          <p className="font-medium">{label}</p>
          <p className="text-primary">
            {formatCurrency(payload[0].value, preferredCurrency)}
          </p>
        </div>
      );
    }
    return null;
  };

  const PieTooltip = ({ active, payload }: { active?: boolean; payload?: Array<{ name: string; value: number }> }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-card border rounded-lg p-3 shadow-custom-md">
          <p className="font-medium">{payload[0].name}</p>
          <p className="text-primary">
            {formatCurrency(payload[0].value, preferredCurrency)}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      <Card className="shadow-custom-md border-0">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                📈 {t('spendingChart.trendsTitle')}
              </CardTitle>
              <CardDescription>
                {t('spendingChart.trendsDescription')}
              </CardDescription>
            </div>
            <Select value={selectedCategory} onValueChange={(value: CategoryFilter) => setSelectedCategory(value)}>
              <SelectTrigger className="w-40 bg-card border-border shadow-custom-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-card border-border shadow-custom-lg z-50">
                {categoryOptions.map((option) => (
                  <SelectItem 
                    key={option.value} 
                    value={option.value}
                    className="hover:bg-accent focus:bg-accent"
                  >
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={barData}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis 
                  dataKey="week" 
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis 
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(value) => `$${value}`}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar 
                  dataKey="total" 
                  fill={getBarColor()}
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-custom-md border-0">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                🥧 {t('spendingChart.distributionTitle')}
              </CardTitle>
              <CardDescription>
                {t('spendingChart.distributionDescription')}
              </CardDescription>
            </div>
            <Select value={selectedPeriod} onValueChange={(value: TimePeriod) => setSelectedPeriod(value)}>
              <SelectTrigger className="w-40 bg-card border-border shadow-custom-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-card border-border shadow-custom-lg z-50">
                {timePeriodOptions.map((option) => (
                  <SelectItem 
                    key={option.value} 
                    value={option.value}
                    className="hover:bg-accent focus:bg-accent"
                  >
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  labelLine={false}
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip content={<PieTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};